import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { RefreshCw, Loader2 } from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { MESSAGE_TYPES, TRACK_STATUSES, statusTone, Sel, Fld } from "./shared";

export default function History({ refs, canWrite }) {
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ property_id: "", tenant_id: "", message_type: "", status: "" });
  const [detail, setDetail] = useState(null);
  const load = useCallback(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) p.append(k, v); });
    api.get(`/messages?${p.toString()}`).then((r) => setRows(r.data)).catch(() => {});
  }, [filters]);
  useEffect(() => { load(); }, [load]);
  const setF = (k, v) => setFilters((s) => ({ ...s, [k]: v }));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Sel testid="hist-property" value={filters.property_id} onChange={(v) => setF("property_id", v)} options={[["", "All properties"], ...refs.properties.map((p) => [p.id, p.name])]} />
        <Sel testid="hist-tenant" value={filters.tenant_id} onChange={(v) => setF("tenant_id", v)} options={[["", "All tenants"], ...refs.tenants.map((t) => [t.id, t.name])]} />
        <Sel testid="hist-type" value={filters.message_type} onChange={(v) => setF("message_type", v)} options={[["", "All types"], ...MESSAGE_TYPES.map((t) => [t, t])]} />
        <Sel testid="hist-status" value={filters.status} onChange={(v) => setF("status", v)} options={[["", "All statuses"], ...["Scheduled", ...TRACK_STATUSES, "Failed"].map((s) => [s, s])]} />
      </div>
      {rows.length === 0 ? <div className="rounded-2xl border border-dashed border-border p-12 text-center text-muted-foreground">No messages yet.</div> : (
        <div className="rounded-2xl border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground"><tr>
              <th className="text-left font-semibold px-4 py-3">Date</th><th className="text-left font-semibold px-4 py-3">Tenant</th><th className="text-left font-semibold px-4 py-3">Type</th><th className="text-left font-semibold px-4 py-3">Method</th><th className="text-left font-semibold px-4 py-3">Status</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} onClick={() => setDetail(r)} data-testid={`message-row-${r.id}`} className="border-t border-border hover:bg-muted/40 cursor-pointer">
                  <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{(r.created_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-3 font-medium">{r.tenant_name}<span className="block text-xs text-muted-foreground">{r.property_name}{r.unit ? ` · ${r.unit}` : ""}</span></td>
                  <td className="px-4 py-3 text-muted-foreground">{r.message_type}</td>
                  <td className="px-4 py-3">{(r.channels || []).map((c) => <Badge key={c} className="rounded-sm mr-1 font-normal bg-muted text-muted-foreground text-[11px]">{c}</Badge>)}</td>
                  <td className="px-4 py-3"><Badge className={`rounded-sm font-normal ${statusTone[r.status] || "bg-muted"}`}>{r.status}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {detail && <MessageDetail msg={detail} canWrite={canWrite} onClose={() => { setDetail(null); load(); }} />}
    </div>
  );
}

function MessageDetail({ msg, canWrite, onClose }) {
  const [m, setM] = useState(msg);
  const [retrying, setRetrying] = useState(false);
  const isFailed = m.status === "Failed" || String(m.email_status || "").startsWith("Failed") || String(m.sms_status || "").startsWith("Failed");
  const setStatus = async (status) => {
    try { const { data } = await api.put(`/messages/${m.id}/status`, { status }); setM(data); toast.success("Status updated"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const retry = async () => {
    setRetrying(true);
    try {
      const { data } = await api.post(`/messages/${m.id}/retry`);
      setM(data);
      toast[data.status === "Sent" ? "success" : "error"](data.status === "Sent" ? "Message re-sent successfully" : "Retry failed — check tenant contact details");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setRetrying(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="message-detail">
        <DialogHeader><DialogTitle>{m.message_type}</DialogTitle><DialogDescription>Message to {m.tenant_name} · {(m.created_at || "").slice(0, 16).replace("T", " ")}</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2 items-center">
            <Badge className={`rounded-sm font-normal ${statusTone[m.status] || "bg-muted"}`} data-testid="detail-status-badge">{m.status}</Badge>
            {(m.channels || []).map((c) => <Badge key={c} className="rounded-sm font-normal bg-muted text-muted-foreground">{c}</Badge>)}
          </div>
          {m.subject && <p className="text-sm font-semibold">{m.subject}</p>}
          <p className="text-sm whitespace-pre-line rounded-lg bg-muted/40 p-3">{m.body}</p>
          <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
            <div>Email: <span className="text-foreground">{m.email_status}</span></div>
            <div>SMS: <span className="text-foreground">{m.sms_status}</span></div>
            <div>Sent by: <span className="text-foreground">{m.sent_by_name}</span></div>
            {m.responded_at && <div>Responded: <span className="text-foreground">{m.responded_at.slice(0, 16).replace("T", " ")}</span></div>}
          </div>
          {m.response_text && <p className="text-sm text-emerald-600">Tenant response: {m.response_text}</p>}
          {(m.attachments || []).length > 0 && <div className="text-xs text-muted-foreground">Attachments: {(m.attachments || []).map((a) => a.name).join(", ")}</div>}
          {canWrite && isFailed && (
            <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/10 dark:border-red-900/40 p-3 flex items-center justify-between gap-3">
              <p className="text-xs text-red-700 dark:text-red-400">This message failed to deliver on one or more channels.</p>
              <Button size="sm" className="rounded-lg shrink-0" onClick={retry} disabled={retrying} data-testid="retry-send-btn">
                {retrying ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />} Retry send
              </Button>
            </div>
          )}
          {canWrite && (
            <Fld label="Update tracking status"><Sel testid="detail-set-status" value={m.status} onChange={setStatus} options={TRACK_STATUSES.map((s) => [s, s])} /></Fld>
          )}
        </div>
        <DialogFooter><Button variant="outline" className="rounded-xl" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
